import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  THead,
  TrHead,
  TBody,
  Th,
  Tr,
  Td,
  InnerCell,
} from "@teamrota/rota-design";

import { StreamProvider } from "@teamrota/shiftstreamlib";

import IconLink from "~/src/containers/payroll/components/IconLink";
import Icon from "~/src/components/icon";
import { EmptyText } from "~/src/components/payroll/style";
import Alarm from "../../components/alarm";

import { SCRAPE } from "./graphql/mutation/scrape";
import { RERUN_ALL } from "./graphql/mutation/rerun-all";

import { ScrollableTable } from "../styles";
import { COL_WIDTHS } from ".";
import { cleanAddress, hasExternalSystem } from "../../utils";
import { shiftStreamClient } from "../../graphql/client";
import { useMutation } from "@apollo/client";
import { ResultModal } from "../../components/result";
import { DangerZoneModal } from "../../components/danger-zone";
import { Role } from "@teamrota/authlib";
import useAuth from "~/src/auth/hooks/use-auth";

interface TableProps {
  accounts: any[];
  loading: boolean;
}

const ONE_HOUR = 60 * 60;
const THREE_HOURS = 3 * ONE_HOUR;
const SIX_HOURS = 6 * ONE_HOUR;
const TWELVE_HOURS = 12 * ONE_HOUR;
const ONE_DAY = 24 * ONE_HOUR;
const FIVE_DAYS = 5 * ONE_DAY;

const Table: React.FC<TableProps> = ({ accounts, loading }) => {
  const history = useHistory();
  const auth = useAuth();

  const isAdmin = auth.hasRole(Role.ROTA_ADMIN);

  const [resultModal, setResultModal] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);
  const [reRunAllAccount, setReRunAllAccount] = useState<any | null>(null);

  const [scrape, { loading: isScrapeLoading }] = useMutation(SCRAPE, {
    client: shiftStreamClient,
  });

  const [rerunAll, { loading: isReRunAllLoading }] = useMutation(RERUN_ALL, {
    client: shiftStreamClient,
  });

  const handleClickStreams = (account: any) => {
    history.push(`/shiftstream/streams/account/${account.id}`);
  };

  const handleClickEmails = (account: any) => {
    history.push(`/shiftstream/emails/account/${account.id}`);
  };

  const handleScrape = async (account: any, action: string) => {
    try {
      const result = await scrape({
        variables: { id: parseInt(account.id), action },
      });
      if (result?.data?.scrape) {
        setResultModal(result.data.scrape);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const handleReRunAll = async (account: any) => {
    try {
      const result = await rerunAll({
        variables: { accountId: parseInt(account.id) },
      });
      const reRunAllResult = result?.data?.reRunAll ?? result?.data?.rerunAll;

      if (reRunAllResult) {
        setResultModal(reRunAllResult);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const handleOpenReRunAllModal = (account: any) => {
    setReRunAllAccount(account);
  };

  const handleCloseReRunAllModal = () => {
    setReRunAllAccount(null);
  };

  const handleProceedReRunAll = async () => {
    if (!reRunAllAccount) {
      return;
    }

    const account = reRunAllAccount;
    handleCloseReRunAllModal();
    await handleReRunAll(account);
  };

  return (
    <>
      <ScrollableTable colWidths={COL_WIDTHS}>
        <THead>
          <TrHead>
            <Th>Account ID</Th>
            <Th>Account Name</Th>
            <Th>Provider</Th>
            <Th>Roster Sync</Th>
            <Th>Unfilled Check</Th>
            <Th>Algo Run</Th>
            <Th>Fulfil Submitted</Th>
            <Th>&nbsp; </Th>
          </TrHead>
        </THead>

        <TBody className="streams-table-scroll-body">
          {accounts?.length ? (
            accounts.map((account: any) => {
              const isEmailProvider = account.provider === StreamProvider.EMAIL;
              const hasExternalLink = hasExternalSystem(account.provider);

              return (
                <Tr key={account.id}>
                  <Td>
                    <InnerCell>#{account.id}</InnerCell>
                  </Td>

                  <Td>
                    <InnerCell>
                      {hasExternalLink ? (
                        <a
                          href={account.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {account.name}
                        </a>
                      ) : (
                        <>{account.name}</>
                      )}

                      <div style={{ fontSize: "70%" }}>
                        {isEmailProvider ? (
                          <>
                            Email:{" "}
                            <span style={{ fontWeight: "bold" }}>
                              {cleanAddress(account.url)}
                            </span>
                          </>
                        ) : hasExternalLink ? (
                          <>
                            Login:{" "}
                            <span style={{ fontWeight: "bold" }}>
                              {account.login}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </InnerCell>
                  </Td>

                  <Td>
                    <img
                      title={account.name}
                      src={`/images/shiftstream/${account.provider}.png`}
                      alt={`${account.provider} logo`}
                      style={{ height: 20, verticalAlign: "middle" }}
                    />
                  </Td>

                  <Td>
                    <InnerCell>
                      <Alarm
                        value={account.metrics.lastRosterSync}
                        warn={THREE_HOURS}
                        alarm={SIX_HOURS}
                        loading={loading || isScrapeLoading}
                        refresh={
                          !isEmailProvider &&
                          (() => handleScrape(account, "roster"))
                        }
                      />
                    </InnerCell>
                  </Td>

                  <Td>
                    <InnerCell>
                      <Alarm
                        value={account.metrics.lastUnfilledCheck}
                        warn={ONE_HOUR}
                        alarm={SIX_HOURS}
                        loading={loading || isScrapeLoading}
                        refresh={
                          !isEmailProvider &&
                          (() => handleScrape(account, "new-shifts"))
                        }
                      />
                    </InnerCell>
                  </Td>

                  <Td>
                    <InnerCell>
                      <Alarm
                        value={account.metrics.lastAlgo}
                        warn={TWELVE_HOURS}
                        alarm={ONE_DAY}
                        loading={loading || isReRunAllLoading}
                        refresh={
                          isAdmin &&
                          !isEmailProvider &&
                          (() => handleOpenReRunAllModal(account))
                        }
                      />
                    </InnerCell>
                  </Td>
                  <Td>
                    <InnerCell>
                      <Alarm
                        value={account.metrics.lastFulfil}
                        warn={ONE_DAY}
                        alarm={FIVE_DAYS}
                      />
                    </InnerCell>
                  </Td>
                  <Td>
                    <InnerCell>
                      {isEmailProvider ? (
                        <IconLink
                          name="EMAIL"
                          size={20}
                          isButton
                          color={Icon.colors.MAIN_ORANGE}
                          tooltip="View Emails"
                          onClick={() => handleClickEmails(account)}
                        />
                      ) : (
                        <IconLink
                          name="ABACUS"
                          size={20}
                          isButton
                          color={Icon.colors.MAIN_ORANGE}
                          tooltip="View streams"
                          onClick={() => handleClickStreams(account)}
                        />
                      )}
                    </InnerCell>
                  </Td>
                </Tr>
              );
            })
          ) : (
            <Tr>
              <Td colSpan={8}>
                <EmptyText>
                  {!loading && "None found 👀"}
                  {loading && "Loading... ⏳"}
                </EmptyText>
              </Td>
            </Tr>
          )}
        </TBody>
      </ScrollableTable>

      {resultModal && (
        <ResultModal
          message={resultModal.message ?? null}
          isError={resultModal.isError ?? false}
          onDismiss={() => setResultModal(null)}
        />
      )}

      {reRunAllAccount && (
        <DangerZoneModal
          isLoading={isReRunAllLoading}
          onCancel={handleCloseReRunAllModal}
          onProceed={handleProceedReRunAll}
        />
      )}
    </>
  );
};

export default Table;
